/**
 * Chart Label Utilities
 *
 * Provides utilities for handling long chart labels with truncation and formatting
 */

/**
 * Truncate a label to a maximum character length and add ellipsis
 *
 * @param label - The label text to truncate
 * @param maxLength - Maximum character length (default: 15)
 * @returns Truncated label with ellipsis if needed
 *
 * @example
 * truncateLabel("Very Long Label Name", 10) // "Very Long..."
 * truncateLabel("Short", 10) // "Short"
 */
export function truncateLabel(label: string | number, maxLength: number = 15): string {
  const text = String(label);

  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Calculate the optimal max label length based on available width and data count
 *
 * Philosophy: Always truncate labels to prevent overflow, even for small datasets.
 * Long labels can cause overlap regardless of dataset size - tooltip is the source of truth.
 *
 * @param dataLength - Number of data points
 * @returns Recommended max character length for labels
 */
export function calculateOptimalLabelLength(dataLength: number): number {
  // Always apply truncation - even small datasets can have long labels
  if (dataLength <= 5) {
    return 12; // Reasonable limit even with few labels
  } else if (dataLength <= 10) {
    return 10;
  } else if (dataLength <= 20) {
    return 8;
  } else {
    return 6; // Very crowded, aggressive truncation
  }
}

/**
 * Create a tick formatter function that truncates labels
 *
 * @param maxLength - Maximum character length for labels
 * @returns A formatter function that can be passed to chart XAxis
 *
 * @example
 * <XAxis tickFormatter={createTruncatingFormatter(12)} />
 */
export function createTruncatingFormatter(maxLength: number = 15) {
  return (value: any) => truncateLabel(value, maxLength);
}
